import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Breadcrumb,
  Button,
  MenuButton,
  Modal,
  ResponsivePrimaryNav,
  SearchResultNavigation,
} from "../../../components/ui";
import {
  FETCH_RESERVATION as fetchReservation,
  FETCH_RESERVATIONS_LIST as fetchReservationsList,
} from "../../../graphql/query/Reservation";
import useSearchResultNavigation from "../../../hooks/useSearchResultNavigation";
import routes from "../pages/Wizard/routes";
import { buildReservationNavigationTargetUrl } from "../searchResultNavigation";
import NewReservation from "./Actions/NewReservation";
import ReservationSearch from "./ReservationSearch";
import { reservationNavigationStore } from "./useReservationFilters";
import useUser from "../../../hooks/useUser";
import { clearSelectionState } from "../../../util/clearSelectionState";
import { scopeBulkActionTargets } from "../../../util/scopeBulkActionTargets";

const RESERVATIONS_NAVIGATION_QUERY = fetchReservationsList({});

const PrimaryNav = props => {
  const { formik } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const pathnames = location?.pathname.split("/");
  const id = pathnames?.length > 2 ? pathnames[2] : null;

  const { hasPermission } = useUser();

  const { data: { reservation } = {} } = useQuery(
    fetchReservation({ createdBy: {}, type: {}, reservationUser: {} }),
    {
      skip: !id,
      variables: { id: +id },
    },
  );

  // find current submodule route
  const path = pathnames[pathnames?.length - 1];
  const route = routes?.find(item => item?.href?.includes(path));
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams.get("status") || "all";
  const listIdParam = queryParams.get("listId");
  const workingListIdParam = queryParams.get("workingListId");
  const listContext = useMemo(
    () => ({
      ...(listIdParam ? { listId: listIdParam } : {}),
      ...(workingListIdParam ? { workingListId: workingListIdParam } : {}),
    }),
    [listIdParam, workingListIdParam],
  );
  const buildReservationListUrl = nextStatus => {
    const query = new URLSearchParams({ status: nextStatus || "all" });
    Object.entries(listContext).forEach(([key, value]) =>
      query.set(key, value),
    );
    return `/reservations?${query.toString()}`;
  };
  const buildReservationDetailsUrl = (recordId, activeSubPage = "details") =>
    buildReservationNavigationTargetUrl({
      activeSubPage,
      listContext,
      recordId,
      status,
    });
  const {
    hasNavigationContext,
    hasNext,
    hasPrevious,
    navigationLoading,
    navigateToNext,
    navigateToPrevious,
    showNavigationControls,
  } = useSearchResultNavigation({
    activeSubPage: route?.href || "details",
    buildTargetUrl: buildReservationNavigationTargetUrl,
    dataKey: "reservationsV2",
    fetchQuery: RESERVATIONS_NAVIGATION_QUERY,
    listContext,
    navigate,
    navigationStore: reservationNavigationStore,
    recordId: id ? Number(id) : null,
    status,
  });
  const listId = +(
    listIdParam || (status === "selectListWorking" ? workingListIdParam : null)
  );

  const selected = Object.keys(formik?.values?.selected?.ids || {});
  const ids = selected?.map(key => {
    const obj = formik?.values?.selected?.ids?.[key];
    return obj?.item?.id || obj?.id;
  });
  const select = formik?.values?.selected?.select;

  const statusMap = {
    all: "All Reservations",
    selectListWorking: "Select List (Working)",
  };
  const label = statusMap[status] || "All Reservations";
  // prepare steps for breadcrumbs
  const steps = [
    {
      label,
      onClick: () => navigate(buildReservationListUrl(status)),
    },
    ...(reservation
      ? [
          {
            label: reservation?.id,
            onClick: () => navigate(buildReservationDetailsUrl(reservation.id)),
          },
          {
            label:
              reservation && reservation.categoryId === 2
                ? "Make Available Details"
                : route?.name,
            onClick: () =>
              navigate(
                buildReservationDetailsUrl(
                  reservation.id,
                  route?.href || "details",
                ),
              ),
          },
        ]
      : []),
  ];

  const breadcrumbProps = {
    currentStepIndex: steps?.length - 1,
    onChange: index => steps[index]?.onClick(),
    steps: steps?.map(item => item?.label),
  };

  // Detail pages share Formik with the list view, so bulk-action props must
  // be scoped to the URL id; otherwise list-side selections / search /
  // listId leak into actions like Export here too. See scopeBulkActionTargets.
  const scopedTargets = scopeBulkActionTargets({
    entityId: id,
    selectedIds: ids,
    fallbacks: { listId, select },
  });

  const defaultActionProps = {
    closeOnBackdrop: false,
    hideCloseButton: true,
    ids: scopedTargets.ids,
    listId: scopedTargets.listId,
    onClose: () => {
      formik?.resetForm();
    },
    scale: "md",
    select: scopedTargets.select,
  };

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const actionMenuProps = {
    label: "Actions",
    options: [
      {
        label: "New Reservation",
        disabled: !hasPermission("CREATE_RESERVATION"),
        modalProps: {
          body: NewReservation,
          disabled: !hasPermission("CREATE_RESERVATION"),
          isCreateOpen,
          onOpen: () => {
            setIsCreateOpen(true);
          },
          onClose: () => {
            setIsCreateOpen(false);
          },
          ...defaultActionProps,
        },
      },
    ],
  };

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchModalProps = {
    body: ReservationSearch,
    closeOnBackdrop: true,
    scale: "md",
    isSearchOpen,
    onOpen: () => {
      setIsSearchOpen(true);
    },
    onClose: () => {
      setIsSearchOpen(false);
    },
    onSearchSubmitted: () => clearSelectionState(formik),
  };

  return (
    <ResponsivePrimaryNav>
      <div>
        <Breadcrumb {...breadcrumbProps} />
      </div>
      <div className="flex items-center gap-2 sm:gap-4 lg:justify-end">
        <SearchResultNavigation
          hasNavigationContext={hasNavigationContext}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          navigationLoading={navigationLoading}
          navigateToNext={navigateToNext}
          navigateToPrevious={navigateToPrevious}
          showNavigationControls={showNavigationControls}
        />
        <Modal {...searchModalProps}>
          <Button label="Reservation Search" action="black" />
        </Modal>
        <MenuButton {...actionMenuProps} />
      </div>
    </ResponsivePrimaryNav>
  );
};

export default PrimaryNav;
